import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { toast } from '@/hooks/use-toast';
import {
  EstablishmentNotice,
  NoticeScope,
  NOTICE_MAX_LENGTH,
} from '@/lib/establishmentNoticeUtils';

export interface EstablishmentNoticeInput {
  message: string;
  scope: NoticeScope;
  specificDate?: string | null;
  weekday?: number | null;
  requireAcknowledgement: boolean;
  isActive: boolean;
}

function mapNotice(row: {
  id: string;
  establishment_id: string;
  message: string;
  scope: string;
  specific_date: string | null;
  weekday: number | null;
  require_acknowledgement: boolean;
  is_active: boolean;
}): EstablishmentNotice {
  return {
    id: row.id,
    establishmentId: row.establishment_id,
    message: row.message,
    scope: row.scope as NoticeScope,
    specificDate: row.specific_date,
    weekday: row.weekday,
    requireAcknowledgement: row.require_acknowledgement,
    isActive: row.is_active,
  };
}

function toRow(input: EstablishmentNoticeInput, establishmentId?: string) {
  return {
    ...(establishmentId ? { establishment_id: establishmentId } : {}),
    message: input.message.slice(0, NOTICE_MAX_LENGTH),
    scope: input.scope,
    specific_date: input.scope === 'date' ? input.specificDate ?? null : null,
    weekday: input.scope === 'weekday' ? input.weekday ?? null : null,
    require_acknowledgement: input.requireAcknowledgement,
    is_active: input.isActive,
  };
}

export function establishmentNoticesQueryKey(establishmentId: string | null | undefined) {
  return ['establishment-notices', establishmentId ?? null] as const;
}

/**
 * Notes optionnelles affichées aux clients.
 * - `usePublicClient` : lecture anonyme (widget public), sans realtime.
 * - Les écritures passent toujours par le client authentifié (staff/admin).
 */
export function useEstablishmentNotices(options: {
  establishmentId?: string | null;
  usePublicClient?: boolean;
}) {
  const { establishmentId, usePublicClient = false } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: establishmentNoticesQueryKey(establishmentId),
    queryFn: async (): Promise<EstablishmentNotice[]> => {
      const client = usePublicClient ? supabasePublic : supabase;
      const { data, error } = await client
        .from('establishment_notices')
        .select('*')
        .eq('establishment_id', establishmentId!)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[useEstablishmentNotices] fetch error:', error);
        return [];
      }
      return (data ?? []).map(mapNotice);
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 60 * 2,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: establishmentNoticesQueryKey(establishmentId) });
  };

  const createNotice = useMutation({
    mutationFn: async (input: EstablishmentNoticeInput) => {
      if (!establishmentId) throw new Error('establishmentId manquant');
      const { error } = await supabase
        .from('establishment_notices')
        .insert({ ...toRow(input, establishmentId), establishment_id: establishmentId });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Note ajoutée', description: 'La note est enregistrée.' });
    },
    onError: (error: unknown) => {
      console.error('[useEstablishmentNotices] create error:', error);
      toast({ title: 'Erreur', description: "Impossible d'ajouter la note.", variant: 'destructive' });
    },
  });

  const updateNotice = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: EstablishmentNoticeInput }) => {
      const { error } = await supabase
        .from('establishment_notices')
        .update(toRow(input))
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Note mise à jour', description: 'Les modifications sont enregistrées.' });
    },
    onError: (error: unknown) => {
      console.error('[useEstablishmentNotices] update error:', error);
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour la note.', variant: 'destructive' });
    },
  });

  const deleteNotice = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('establishment_notices').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Note supprimée' });
    },
    onError: (error: unknown) => {
      console.error('[useEstablishmentNotices] delete error:', error);
      toast({ title: 'Erreur', description: 'Impossible de supprimer la note.', variant: 'destructive' });
    },
  });

  return {
    notices: query.data ?? [],
    isLoading: query.isLoading,
    createNotice: createNotice.mutateAsync,
    updateNotice: updateNotice.mutateAsync,
    deleteNotice: deleteNotice.mutateAsync,
    isSaving: createNotice.isPending || updateNotice.isPending || deleteNotice.isPending,
  };
}
